import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import {
  MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './LocationMap.css';

const INDIA_CENTER = [20.5937, 78.9629];
const DEFAULT_ZOOM = 16;

const pinIcon = L.divIcon({
  className: 'gp-map-pin-shell',
  html: '<span class="gp-map-pin" aria-hidden="true"></span>',
  iconSize: [34, 42],
  iconAnchor: [17, 40],
  popupAnchor: [0, -38],
});

const finiteCoordinate = (value, min, max) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= min && number <= max ? number : null;
};

export const normalizeLocation = (latitude, longitude) => {
  const lat = finiteCoordinate(latitude, -90, 90);
  const lng = finiteCoordinate(longitude, -180, 180);
  return lat === null || lng === null ? null : [lat, lng];
};

export const directionsUrl = (latitude, longitude, address = '') => {
  const location = normalizeLocation(latitude, longitude);
  const query = location ? location.join(',') : String(address || '').trim();
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
};

function Recenter({ position, zoom }) {
  const map = useMap();
  const lastPosition = useRef('');
  useEffect(() => {
    if (!map) return;
    if (typeof map.invalidateSize === 'function') {
      map.invalidateSize();
    }
    if (!position) return;
    const key = position.join(',');
    if (key === lastPosition.current) return;
    lastPosition.current = key;
    map.setView(position, zoom, { animate: false });
  }, [map, position, zoom]);
  return null;
}

function MapAutoResizer() {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;

    const triggerInvalidate = () => {
      if (typeof map.invalidateSize === 'function') {
        map.invalidateSize();
      }
    };

    triggerInvalidate();

    const rafId = typeof requestAnimationFrame !== 'undefined'
      ? requestAnimationFrame(triggerInvalidate)
      : null;

    const timer1 = setTimeout(triggerInvalidate, 120);
    const timer2 = setTimeout(triggerInvalidate, 350);
    const timer3 = setTimeout(triggerInvalidate, 800);

    let observer;
    if (typeof ResizeObserver !== 'undefined' && typeof map.getContainer === 'function') {
      const container = map.getContainer();
      if (container) {
        observer = new ResizeObserver(() => {
          triggerInvalidate();
        });
        observer.observe(container);
      }
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      observer?.disconnect();
    };
  }, [map]);

  return null;
}

function PickEvents({ disabled, onPick }) {
  useMapEvents({
    click(event) {
      if (!disabled) onPick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

const TILE_ATTRIBUTION = 'Tiles &copy; <a href="https://www.esri.com" target="_blank" rel="noopener noreferrer">Esri</a> &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom';
const TILE_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}';

export function LocationPicker({
  latitude,
  longitude,
  onChange,
  disabled = false,
  label = 'नकाशावरील स्थान',
  help = 'नकाशावर क्लिक करा, स्थानदर्शक खूण ओढा किंवा सध्याचे स्थान वापरा.',
  defaultCenter = INDIA_CENTER,
  id = 'location',
}) {
  const selected = normalizeLocation(latitude, longitude);
  const center = selected || normalizeLocation(defaultCenter?.[0], defaultCenter?.[1]) || INDIA_CENTER;
  const mapRef = useRef(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');

  const pick = (lat, lng) => {
    setError('');
    onChange?.({
      latitude: Number(lat).toFixed(6),
      longitude: Number(lng).toFixed(6),
    });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('या ब्राउझरमध्ये सध्याचे स्थान शोधण्याची सुविधा उपलब्ध नाही.');
      return;
    }
    setLocating(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      position => {
        const next = [position.coords.latitude, position.coords.longitude];
        pick(next[0], next[1]);
        mapRef.current?.setView(next, DEFAULT_ZOOM);
        setLocating(false);
      },
      locationError => {
        const denied = locationError.code === locationError.PERMISSION_DENIED;
        setError(denied
          ? 'स्थान वापरण्याची परवानगी नाकारली आहे. ब्राउझरमध्ये परवानगी द्या किंवा नकाशावर स्थान निवडा.'
          : 'सध्याचे स्थान मिळाले नाही. पुन्हा प्रयत्न करा किंवा नकाशावर स्थान निवडा.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  };

  return <section className="gp-location-picker" aria-labelledby={`${id}-label`}>
    <div className="gp-location-heading">
      <div><strong id={`${id}-label`}>{label}</strong><small>{help}</small></div>
      <div className="gp-location-actions">
        <button type="button" onClick={useCurrentLocation} disabled={disabled || locating}>{locating ? 'स्थान शोधत आहे…' : '⌖ सध्याचे स्थान'}</button>
        {selected && <button type="button" className="secondary" onClick={() => onChange?.({ latitude: '', longitude: '' })} disabled={disabled}>स्थान काढा</button>}
      </div>
    </div>
    <div className="gp-map-frame">
      <MapContainer ref={mapRef} center={center} zoom={selected ? DEFAULT_ZOOM : 5} scrollWheelZoom className="gp-map" attributionControl>
        <TileLayer
          attribution={TILE_ATTRIBUTION}
          url={TILE_URL}
          maxZoom={19}
        />
        <MapAutoResizer />
        <Recenter position={selected} zoom={DEFAULT_ZOOM} />
        <PickEvents disabled={disabled} onPick={pick} />
        {selected && <Marker
          position={selected}
          icon={pinIcon}
          draggable={!disabled}
          eventHandlers={{ dragend: event => { const point = event.target.getLatLng(); pick(point.lat, point.lng); } }}
        ><Popup>निवडलेले स्थान</Popup></Marker>}
      </MapContainer>
    </div>
    <div className="gp-location-readout" aria-live="polite">
      {selected ? <><span>अक्षांश <b>{selected[0].toFixed(6)}</b></span><span>रेखांश <b>{selected[1].toFixed(6)}</b></span></> : <span>अद्याप स्थान निवडलेले नाही.</span>}
    </div>
    {error && <p className="gp-location-error" role="alert">{error}</p>}
  </section>;
}

export function LocationMap({ latitude, longitude, title = 'स्थान', address = '', compact = false }) {
  const position = useMemo(() => normalizeLocation(latitude, longitude), [latitude, longitude]);
  if (!position) return null;
  const directions = directionsUrl(latitude, longitude, address);
  return <section className={`gp-location-map ${compact ? 'compact' : ''}`} aria-label={`${title} नकाशा`}>
    <MapContainer center={position} zoom={DEFAULT_ZOOM} scrollWheelZoom={false} className="gp-map" attributionControl>
      <TileLayer
        attribution={TILE_ATTRIBUTION}
        url={TILE_URL}
        maxZoom={19}
      />
      <MapAutoResizer />
      <Marker position={position} icon={pinIcon}><Popup><strong>{title}</strong>{address && <><br />{address}</>}</Popup></Marker>
    </MapContainer>
    {directions && <a className="gp-map-directions" href={directions} target="_blank" rel="noreferrer">दिशा मिळवा ↗</a>}
  </section>;
}

export function GoogleLocationMap({ latitude, longitude, title = 'स्थान', address = '', compact = false }) {
  const position = useMemo(() => normalizeLocation(latitude, longitude), [latitude, longitude]);
  if (!position) return null;
  const directions = directionsUrl(latitude, longitude, address);
  const query = encodeURIComponent(position.join(','));
  const embedUrl = `https://www.google.com/maps?q=${query}&z=${compact ? 15 : 16}&output=embed`;

  return <section className={`gp-location-map gp-google-location-map ${compact ? 'compact' : ''}`} aria-label={`${title} नकाशा`}>
    <iframe
      className="gp-map"
      title={`${title} — Google Maps`}
      src={embedUrl}
      loading="lazy"
      allowFullScreen
      referrerPolicy="no-referrer-when-downgrade"
    />
    {directions && <a className="gp-map-directions" href={directions} target="_blank" rel="noopener noreferrer">दिशा मिळवा ↗</a>}
  </section>;
}
